import { create } from "zustand";

import type { AppSchema, ComponentNode, Page, DataModel, Theme, Permission, EventBinding } from "@shared/types";

import { applyPatchToSchema, type JsonPatchOperation } from "./operation";
import { createComponentNode, createDefaultApp, createDefaultDataModel, createDefaultPage } from "./schema";

interface BuilderState {
  schema: AppSchema;
  selectedNodeId: string | null;
  activePageId: string | null;
  previewMode: boolean;
  undoStack: AppSchema[];
  redoStack: AppSchema[];
  canUndo: boolean;
  canRedo: boolean;
  _skipHistory: boolean;
  setSchema: (schema: AppSchema, options?: { skipHistory?: boolean }) => void;
  applyRemoteSchema: (schema: AppSchema) => void;
  applyRemotePatch: (patch: JsonPatchOperation[]) => void;
  pushHistory: (prevSchema: AppSchema) => void;
  undo: () => void;
  redo: () => void;
  selectNode: (id: string | null) => void;
  setActivePage: (id: string) => void;
  setPreviewMode: (value: boolean) => void;
  addPage: () => void;
  updatePage: (id: string, patch: Partial<Page>) => void;
  deletePage: (id: string) => void;
  addChild: (parentId: string, type: string, index?: number) => void;
  moveChild: (parentId: string, fromIndex: number, toIndex: number) => void;
  updateNodeProps: (nodeId: string, props: Record<string, unknown>) => void;
  updateNodeStyle: (nodeId: string, style: Record<string, unknown>) => void;
  updateNodeEvents: (nodeId: string, events: EventBinding[]) => void;
  deleteNode: (nodeId: string) => void;
  addDataModel: () => void;
  updateDataModel: (id: string, patch: Partial<DataModel>) => void;
  deleteDataModel: (id: string) => void;
  updateTheme: (theme: Partial<Theme>) => void;
  updatePermissions: (permissions: Permission[]) => void;
  findNodePath: (nodeId: string) => { page: Page; parent: ComponentNode | null; index: number; node: ComponentNode } | null;
}

function visitNode(
  node: ComponentNode,
  callback: (n: ComponentNode, parent: ComponentNode | null) => void,
  parent: ComponentNode | null = null,
) {
  callback(node, parent);
  node.children?.forEach((child) => visitNode(child, callback, node));
}

function findNodeInTree(
  root: ComponentNode,
  nodeId: string,
): { parent: ComponentNode | null; index: number; node: ComponentNode } | null {
  if (root.id === nodeId) {
    return { parent: null, index: -1, node: root };
  }
  for (let i = 0; i < (root.children?.length ?? 0); i++) {
    const child = root.children![i];
    if (child.id === nodeId) {
      return { parent: root, index: i, node: child };
    }
    const found = findNodeInTree(child, nodeId);
    if (found) return found;
  }
  return null;
}

function removeNodeFromTree(root: ComponentNode, nodeId: string): ComponentNode {
  if (root.id === nodeId) {
    return root; // cannot remove root this way
  }
  if (!root.children) return root;
  return {
    ...root,
    children: root.children
      .filter((c) => c.id !== nodeId)
      .map((c) => removeNodeFromTree(c, nodeId)),
  };
}

function addChildToTree(root: ComponentNode, parentId: string, child: ComponentNode, index?: number): ComponentNode {
  if (root.id === parentId) {
    const children = root.children ? [...root.children] : [];
    const insertIndex = index ?? children.length;
    children.splice(insertIndex, 0, child);
    return { ...root, children };
  }
  if (!root.children) return root;
  return {
    ...root,
    children: root.children.map((c) => addChildToTree(c, parentId, child, index)),
  };
}

function moveChildInTree(
  root: ComponentNode,
  parentId: string,
  fromIndex: number,
  toIndex: number,
): ComponentNode {
  if (root.id === parentId && root.children) {
    const children = [...root.children];
    const [moved] = children.splice(fromIndex, 1);
    const safeTo = Math.max(0, Math.min(toIndex, children.length));
    children.splice(safeTo, 0, moved);
    return { ...root, children };
  }
  if (!root.children) return root;
  return {
    ...root,
    children: root.children.map((c) => moveChildInTree(c, parentId, fromIndex, toIndex)),
  };
}

function updateNodeInTree(
  root: ComponentNode,
  nodeId: string,
  updater: (node: ComponentNode) => ComponentNode,
): ComponentNode {
  if (root.id === nodeId) {
    return updater(root);
  }
  if (!root.children) return root;
  return {
    ...root,
    children: root.children.map((c) => updateNodeInTree(c, nodeId, updater)),
  };
}

const initialSchema = createDefaultApp("dev-tenant");

export const useBuilderStore = create<BuilderState>((set, get) => ({
  schema: initialSchema,
  selectedNodeId: null,
  activePageId: initialSchema.pages[0]?.id ?? null,
  previewMode: false,
  undoStack: [],
  redoStack: [],
  canUndo: false,
  canRedo: false,
  _skipHistory: false,

  setSchema: (schema, options = {}) =>
    set({ schema, activePageId: schema.pages[0]?.id ?? null, _skipHistory: options.skipHistory ?? false }),

  applyRemoteSchema: (schema) =>
    set({ schema, activePageId: schema.pages[0]?.id ?? null, _skipHistory: true }),

  applyRemotePatch: (patch) =>
    set((state) => {
      const merged = applyPatchToSchema(state.schema, patch);
      if (!merged) return state;
      return { schema: merged, _skipHistory: true };
    }),

  pushHistory: (prevSchema) =>
    set((state) => ({
      undoStack: [...state.undoStack, prevSchema],
      redoStack: [],
      canUndo: true,
      canRedo: false,
    })),

  undo: () =>
    set((state) => {
      if (state.undoStack.length === 0) return state;
      const prevSchema = state.undoStack[state.undoStack.length - 1];
      const nextUndo = state.undoStack.slice(0, -1);
      return {
        schema: prevSchema,
        activePageId: prevSchema.pages[0]?.id ?? null,
        undoStack: nextUndo,
        redoStack: [...state.redoStack, state.schema],
        canUndo: nextUndo.length > 0,
        canRedo: true,
        _skipHistory: true,
      };
    }),

  redo: () =>
    set((state) => {
      if (state.redoStack.length === 0) return state;
      const nextSchema = state.redoStack[state.redoStack.length - 1];
      const nextRedo = state.redoStack.slice(0, -1);
      return {
        schema: nextSchema,
        activePageId: nextSchema.pages[0]?.id ?? null,
        undoStack: [...state.undoStack, state.schema],
        redoStack: nextRedo,
        canUndo: true,
        canRedo: nextRedo.length > 0,
        _skipHistory: true,
      };
    }),

  selectNode: (id) => set({ selectedNodeId: id }),
  setActivePage: (id) => set({ activePageId: id, selectedNodeId: null }),
  setPreviewMode: (value) => set({ previewMode: value }),

  addPage: () =>
    set((state) => {
      const page = createDefaultPage();
      return { schema: { ...state.schema, pages: [...state.schema.pages, page] }, activePageId: page.id };
    }),

  updatePage: (id, patch) =>
    set((state) => ({
      schema: {
        ...state.schema,
        pages: state.schema.pages.map((p) => (p.id === id ? { ...p, ...patch } : p)),
      },
    })),

  deletePage: (id) =>
    set((state) => {
      if (state.schema.pages.length <= 1) return state;
      const nextPages = state.schema.pages.filter((p) => p.id !== id);
      const nextActive = nextPages[0]?.id ?? null;
      return {
        schema: { ...state.schema, pages: nextPages },
        activePageId: nextActive,
        selectedNodeId: null,
      };
    }),

  addChild: (parentId, type, index) =>
    set((state) => {
      const page = state.schema.pages.find((p) => p.id === state.activePageId);
      if (!page) return state;
      const child = createComponentNode(type);
      return {
        schema: {
          ...state.schema,
          pages: state.schema.pages.map((p) =>
            p.id === state.activePageId
              ? { ...p, layout: addChildToTree(p.layout, parentId, child, index) }
              : p,
          ),
        },
        selectedNodeId: child.id,
      };
    }),

  moveChild: (parentId, fromIndex, toIndex) =>
    set((state) => ({
      schema: {
        ...state.schema,
        pages: state.schema.pages.map((p) =>
          p.id === state.activePageId
            ? { ...p, layout: moveChildInTree(p.layout, parentId, fromIndex, toIndex) }
            : p,
        ),
      },
    })),

  updateNodeProps: (nodeId, props) =>
    set((state) => ({
      schema: {
        ...state.schema,
        pages: state.schema.pages.map((p) =>
          p.id === state.activePageId
            ? { ...p, layout: updateNodeInTree(p.layout, nodeId, (n) => ({ ...n, props: { ...n.props, ...props } })) }
            : p,
        ),
      },
    })),

  updateNodeStyle: (nodeId, style) =>
    set((state) => ({
      schema: {
        ...state.schema,
        pages: state.schema.pages.map((p) =>
          p.id === state.activePageId
            ? {
                ...p,
                layout: updateNodeInTree(p.layout, nodeId, (n) => ({
                  ...n,
                  style: { ...n.style, ...style },
                })),
              }
            : p,
        ),
      },
    })),

  updateNodeEvents: (nodeId, events) =>
    set((state) => ({
      schema: {
        ...state.schema,
        pages: state.schema.pages.map((p) =>
          p.id === state.activePageId
            ? { ...p, layout: updateNodeInTree(p.layout, nodeId, (n) => ({ ...n, events })) }
            : p,
        ),
      },
    })),

  deleteNode: (nodeId) =>
    set((state) => ({
      schema: {
        ...state.schema,
        pages: state.schema.pages.map((p) =>
          p.id === state.activePageId
            ? { ...p, layout: removeNodeFromTree(p.layout, nodeId) }
            : p,
        ),
      },
      selectedNodeId: state.selectedNodeId === nodeId ? null : state.selectedNodeId,
    })),

  addDataModel: () =>
    set((state) => ({
      schema: { ...state.schema, dataModels: [...state.schema.dataModels, createDefaultDataModel()] },
    })),

  updateDataModel: (id, patch) =>
    set((state) => ({
      schema: {
        ...state.schema,
        dataModels: state.schema.dataModels.map((m) => (m.id === id ? { ...m, ...patch } : m)),
      },
    })),

  deleteDataModel: (id) =>
    set((state) => ({
      schema: { ...state.schema, dataModels: state.schema.dataModels.filter((m) => m.id !== id) },
    })),

  updateTheme: (theme) =>
    set((state) => ({
      schema: { ...state.schema, theme: { ...state.schema.theme, ...theme } },
    })),

  updatePermissions: (permissions) =>
    set((state) => ({
      schema: { ...state.schema, permissions },
    })),

  findNodePath: (nodeId) => {
    const state = get();
    const page = state.schema.pages.find((p) => p.id === state.activePageId);
    if (!page) return null;
    const found = findNodeInTree(page.layout, nodeId);
    if (!found) return null;
    return { page, ...found };
  },
}));

useBuilderStore.subscribe((state, prevState) => {
  if (!prevState) return;
  if (state._skipHistory || state.schema === prevState.schema) {
    if (state._skipHistory) {
      useBuilderStore.setState({ _skipHistory: false });
    }
    return;
  }
  state.pushHistory(prevState.schema);
});

export { visitNode };
